import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import useChemical from 'hooks/useChemical';
import type { EnrichedChemical } from 'hooks/useChemical/types';
import useMix from 'hooks/useMix';
import type { Mix, MixChemical } from 'hooks/useMix/types';
import type {
    MixRecipe,
    MixRecipeProduct,
    MixRecipeProductCategory,
    MixRecipeProductSignalWord,
    MixRecipeRateUnit
} from 'hooks/useMixRecipe/types';

import MixApplyPage from 'pages/MixApplyPage';

import { TEST_ID } from './constants';
import type { FunctionComponent } from './types';

const CATEGORY_VALUES: readonly MixRecipeProductCategory[] = [
    'adjuvant',
    'fungicide',
    'growth',
    'herbicide',
    'insecticide',
    'surfactant'
];

const SIGNAL_VALUES: readonly MixRecipeProductSignalWord[] = ['caution', 'danger', 'warning'];

const RATE_UNITS: readonly MixRecipeRateUnit[] = ['oz/ac', 'lb/ac', 'gal/ac', 'pt/ac', 'qt/ac'];

const normalizeCategory = (
    raw: string | null | undefined
): MixRecipeProductCategory | undefined => {
    if (!raw) {
        return undefined;
    }

    const match = CATEGORY_VALUES.find(value => value === raw.toLowerCase());

    return match;
};

const normalizeSignalWord = (
    raw: string | null | undefined
): MixRecipeProductSignalWord | undefined => {
    if (!raw) {
        return undefined;
    }

    const match = SIGNAL_VALUES.find(value => value === raw.toLowerCase());

    return match;
};

const normalizeRateUnit = (rateUnit: string, ratePerUnit: string): MixRecipeRateUnit => {
    const key = `${rateUnit}/${ratePerUnit === 'acre' ? 'ac' : ratePerUnit}`.toLowerCase();
    const match = RATE_UNITS.find(value => value === key);

    return match ?? 'oz/ac';
};

const buildProduct = (
    chemical: MixChemical,
    enriched: EnrichedChemical | null
): MixRecipeProduct => ({
    category: normalizeCategory(chemical.classification),
    cropMatchRateRange: undefined,
    epaRegNumber: chemical.epaNumber ?? '',
    formulation: enriched?.formulationName ?? undefined,
    isDicamba: undefined,
    isRestrictedUse: enriched?.isRestrictedUse ?? undefined,
    labelRateRange:
        enriched?.genericMinRate !== null && enriched?.genericMaxRate !== null && enriched
            ? { maximum: enriched.genericMaxRate ?? 0, minimum: enriched.genericMinRate ?? 0 }
            : undefined,
    manufacturer: enriched?.manufacturer ?? undefined,
    modeOfActionCode: enriched?.modeOfActions?.[0] ?? undefined,
    productId: chemical.chemicalId,
    productName: chemical.labelName,
    rate: chemical.applicationRate,
    rateUnit: normalizeRateUnit(chemical.rateUnit, chemical.ratePerUnit),
    signalWord: normalizeSignalWord(enriched?.signalWord)
});

const buildRecipe = async (
    mix: Mix,
    fetchChemical: (params: { id: string }) => Promise<EnrichedChemical | null>
): Promise<MixRecipe> => {
    const enriched = await Promise.all(
        mix.chemicals.map(chemical =>
            chemical.chemicalType === 'ENRICHED'
                ? fetchChemical({ id: chemical.chemicalId })
                : Promise.resolve(null)
        )
    );

    return {
        mixRecipeId: mix.id,
        name: mix.name,
        products: mix.chemicals.map((chemical, index) => buildProduct(chemical, enriched[index]))
    };
};

const ApplyFromMixRoute: FunctionComponent = ({ testId = TEST_ID }) => {
    const { mixId } = useParams<{ mixId: string }>();
    const { fetchMix } = useMix();
    const { fetchChemical } = useChemical();
    const [recipe, setRecipe] = useState<MixRecipe | null>(null);

    useEffect(() => {
        if (!mixId) {
            return;
        }

        let cancelled = false;

        (async () => {
            const mix = await fetchMix({ id: mixId });

            if (!mix || cancelled) {
                return;
            }

            const built = await buildRecipe(mix, fetchChemical);

            if (!cancelled) {
                setRecipe(built);
            }
        })();

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mixId]);

    return <MixApplyPage overrideMixRecipe={recipe ?? undefined} testId={testId} />;
};

export default ApplyFromMixRoute;
